import { Helmet } from "react-helmet-async";
import { Button } from "@mineoptics/shared-ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@mineoptics/shared-ui";
import { CheckCircle, ArrowRight, Sparkles } from "lucide-react";

const pricingTiers = [{
  name: "Starter",
  price: "$99",
  period: "/month",
  description: "Perfect for small operations getting started",
  features: ["Up to 5 users", "Cost model", "Email support", "1 project"],
  cta: "Start Free Trial"
}, {
  name: "Professional",
  price: "$299",
  period: "/month",
  description: "For growing mining operations at scale",
  features: ["Up to 25 users", "Advanced cost model", "Priority support", "Unlimited projects", "API access"],
  popular: true,
  cta: "Start Free Trial"
}, {
  name: "Enterprise",
  price: "Custom",
  period: "",
  description: "For large-scale operations with custom needs",
  features: ["Unlimited users", "All features", "24/7 support", "Custom integrations", "Dedicated account manager", "SSO & SAML"],
  cta: "Contact Sales"
}];

export default function Pricing() {
  const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.mineoptics.com';

  return <>
    <Helmet>
      <title>Pricing | MineOptics</title>
      <meta
        name="description"
        content="Simple, transparent pricing that scales with your mining operation. Start free, upgrade when you need."
      />
    </Helmet>

    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
          <a href="/" className="flex items-center gap-2.5 group">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25 transition-transform group-hover:scale-105">
              M
            </div>
            <span className="font-semibold text-xl tracking-tight">MineOptics</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/#features" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1">
              Features
            </a>
            <a href="/pricing" className="nav-link text-sm font-medium text-foreground transition-colors pb-1 border-b-2 border-primary">
              Pricing
            </a>
            <a href="https://docs.mineoptics.com" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1" target="_blank" rel="noopener noreferrer">
              Docs
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="hidden sm:inline-flex font-medium" asChild>
              <a href={`${APP_URL}/auth/login`}>Sign in</a>
            </Button>
            <Button className="btn-gradient text-white border-0 shadow-lg shadow-primary/25 font-medium" asChild>
              <a href={`${APP_URL}/auth/signup`}>
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="pt-32 pb-24 md:pb-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Simple Pricing</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Plans that scale
              <br />
              <span className="gradient-text">with your operation</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start free, upgrade when you need. No hidden fees, no surprises.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <Card
                key={index}
                className={`pricing-card relative border-0 shadow-soft hover-lift overflow-hidden ${
                  tier.popular
                    ? "popular ring-2 ring-primary shadow-xl shadow-primary/10"
                    : "bg-white"
                }`}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-accent" />
                )}
                {tier.popular && (
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary text-white">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="pb-4 pt-8 px-8">
                  <CardTitle className="text-xl font-semibold">{tier.name}</CardTitle>
                  <CardDescription className="text-base">{tier.description}</CardDescription>
                  <div className="mt-6 mb-2">
                    <span className="text-5xl font-bold tracking-tight">{tier.price}</span>
                    <span className="text-muted-foreground text-lg">{tier.period}</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 px-8 pb-8">
                  <ul className="space-y-4 mb-8">
                    {tier.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full font-medium h-12 ${
                      tier.popular
                        ? "btn-gradient text-white border-0 shadow-lg shadow-primary/25"
                        : "bg-secondary hover:bg-secondary/80 text-foreground"
                    }`}
                    asChild
                  >
                    <a href={tier.cta === "Contact Sales" ? "/contact" : `${APP_URL}/auth/signup`}>
                      {tier.cta}
                      {tier.cta !== "Contact Sales" && <ArrowRight className="ml-2 h-4 w-4" />}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="mt-24 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-8">
              <div className="p-6 rounded-2xl bg-white/80 backdrop-blur-sm shadow-soft">
                <h3 className="text-lg font-semibold mb-3">Can I switch plans later?</h3>
                <p className="text-muted-foreground">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and billing adjusts accordingly.</p>
              </div>
              <div className="p-6 rounded-2xl bg-white/80 backdrop-blur-sm shadow-soft">
                <h3 className="text-lg font-semibold mb-3">What payment methods do you accept?</h3>
                <p className="text-muted-foreground">We accept all major credit cards, ACH transfers, and purchase orders for Enterprise customers.</p>
              </div>
              <div className="p-6 rounded-2xl bg-white/80 backdrop-blur-sm shadow-soft">
                <h3 className="text-lg font-semibold mb-3">Is there a free trial?</h3>
                <p className="text-muted-foreground">Yes, all plans include a 14-day free trial with no credit card required. You'll have full access to all features during the trial period.</p>
              </div>
              <div className="p-6 rounded-2xl bg-white/80 backdrop-blur-sm shadow-soft">
                <h3 className="text-lg font-semibold mb-3">What happens when I exceed my user limit?</h3>
                <p className="text-muted-foreground">We'll notify you when you're approaching your user limit. You can easily upgrade to the next tier to add more users.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 gradient-bg" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

        <div className="relative container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to get started?
            </h2>
            <p className="text-lg md:text-xl mb-10 text-white/80">
              Join leading mining operations using MineOptics to optimize their costs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 font-medium text-base px-8 h-12 shadow-xl" asChild>
                <a href={`${APP_URL}/auth/signup`}>
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-medium text-base px-8 h-12 bg-transparent" asChild>
                <a href="/contact">Contact Sales</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 md:py-20 bg-muted/30 border-t">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-5 gap-12 md:gap-8">
            <div className="md:col-span-2">
              <a href="/" className="flex items-center gap-2.5 mb-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25">
                  M
                </div>
                <span className="font-semibold text-xl tracking-tight">MineOptics</span>
              </a>
              <p className="text-muted-foreground max-w-sm leading-relaxed">
                The modern platform for mining cost modeling. Built for teams who want to work smarter, not harder.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Product</h3>
              <ul className="space-y-3">
                <li><a href="/#features" className="text-muted-foreground hover:text-primary transition-colors">Features</a></li>
                <li><a href="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</a></li>
                <li><a href="https://docs.mineoptics.com" className="text-muted-foreground hover:text-primary transition-colors" target="_blank" rel="noopener noreferrer">Documentation</a></li>
                <li><a href={`${APP_URL}`} className="text-muted-foreground hover:text-primary transition-colors">Dashboard</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Company</h3>
              <ul className="space-y-3">
                <li><a href="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</a></li>
                <li><a href="/about" className="text-muted-foreground hover:text-primary transition-colors">About</a></li>
                <li><a href="/careers" className="text-muted-foreground hover:text-primary transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Legal</h3>
              <ul className="space-y-3">
                <li><a href="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy</a></li>
                <li><a href="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms</a></li>
                <li><a href="/security" className="text-muted-foreground hover:text-primary transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2024 MineOptics. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  </>;
}
